import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { Integration } from "model/integration";
import { FollowUpBossAgentSelection } from "model/follow-up-boss-agent-selection";
import { ConnectionTab } from "./tabs/connection";
import { SettingsTab } from "./tabs/settings";

type AgentSync = "off" | "sync_data" | "sync_data_and_create";
type MergeStrategy = "fill_in_blanks" | "fub_wins" | "ihf_wins";

// Design-system tabs (Figma Tabs group): 16px/400 labels, active 1C1B1F with a
// 2px E85F2A underline, inactive 6C6C6C, 16px l/r padding, no gap between tabs
const useTabStyles = makeStyles()({
	tabs: {
		minHeight: 32,
	},
	tab: {
		minWidth: 0,
		minHeight: 32,
		padding: "4px 16px",
		fontSize: 16,
		fontWeight: 400,
		color: "#6C6C6C",
		opacity: 1,
		borderBottom: "2px solid #EBEBEB",
		"&.Mui-selected": {
			color: "#1C1B1F",
		},
	},
});

function ModalTabs({ value, onChange }: { value: number; onChange: (value: number) => void }) {
	const { classes } = useTabStyles();
	return (
		<Mui.Tabs
			value={value}
			onChange={(_e, newValue) => onChange(newValue)}
			className={classes.tabs}
			TabIndicatorProps={{ style: { backgroundColor: "#E85F2A", height: 2 } }}
		>
			<Mui.Tab label="Status" className={classes.tab} disableRipple />
			<Mui.Tab label="Settings" className={classes.tab} disableRipple />
		</Mui.Tabs>
	);
}

interface Props {
	open: boolean;
	onClose: () => void;
	integration: Integration;
	connected: boolean | null;
	webhooksMissing: number;
	webhookLastHealthCheckDate: number | null;
	agentsMatched: number | null;
	ihfAgentsNotInFub: number | null;
	fubUsersNotInIhf: number | null;
	leadsMatched: number | null;
	leadsUnmatched: number | null;
	backfillStatus: "not_started" | "running" | "complete" | "failed" | null;
	agentSync: AgentSync;
	mergeStrategy?: MergeStrategy;
	apiKeyValid: boolean | null;
	lastWebhookProcessed: number | null;
	lastSyncDate: number | null;
	errorCount24h: number;
	backfillLeadsStatus: string | null;
	backfillEventsStatus: string | null;
	onStatusChange: (patch: {
		connected?: boolean;
		webhooksMissing?: number;
		webhookLastHealthCheckDate?: number | null;
		agentsMatched?: number | null;
		ihfAgentsNotInFub?: number | null;
		fubUsersNotInIhf?: number | null;
		leadsMatched?: number | null;
		leadsUnmatched?: number | null;
		backfillStatus?: "not_started" | "running" | "complete" | "failed" | null;
		agentSync?: AgentSync;
		apiKeyValid?: boolean | null;
		lastWebhookProcessed?: number | null;
		lastSyncDate?: number | null;
		errorCount24h?: number;
		backfillLeadsStatus?: string | null;
		backfillEventsStatus?: string | null;
		mergeStrategy?: MergeStrategy;
	}) => void;
}

interface State {
	activeTab: number;
	// MAX-4236: last loaded agent selection, kept across tab switches (the Settings tab remounts)
	agentSelection: FollowUpBossAgentSelection | null;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			activeTab: 0,
			agentSelection: null,
		};
	}

	public componentDidUpdate(prevProps: Props) {
		// The dialog stays mounted between opens. A closed modal or a connection change (disconnect,
		// reconnect) makes the cached selection stale; a disconnected integration has no tabs.
		if (prevProps.open && !this.props.open) {
			this.setState({ agentSelection: null });
		}
		if (prevProps.connected !== this.props.connected) {
			this.setState({ agentSelection: null, activeTab: 0 });
		}
	}

	public render() {
		const { open, onClose, integration, connected, webhooksMissing, webhookLastHealthCheckDate,
				agentsMatched, ihfAgentsNotInFub, fubUsersNotInIhf, leadsMatched, leadsUnmatched,
				backfillStatus, agentSync, mergeStrategy, apiKeyValid, lastWebhookProcessed, lastSyncDate,
				errorCount24h, backfillLeadsStatus, backfillEventsStatus, onStatusChange } = this.props;
		const { activeTab } = this.state;

		const isWizard = connected === false;
		const title = isWizard ? `Connect to ${integration.label}` : integration.label;

		return (
			<Mui.Dialog
				open={open}
				onClose={onClose}
				fullWidth
				maxWidth="md"
				// Top-anchored with a fixed offset: content-driven height grows downward
				// instead of re-centering as wizard steps change (MAX-3965)
				PaperProps={{ style: { alignSelf: "flex-start", marginTop: 80, maxHeight: "calc(100% - 112px)" } }}
			>
				<Mui.DialogTitle>
					<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
						<Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
							{integration.integrationType.iconUrl && (
								<img
									src={integration.integrationType.iconUrl}
									alt=""
									style={{ width: 24, height: 24, objectFit: "contain" }}
								/>
							)}
							<Mui.Typography variant="h6" style={{ fontSize: 24, fontWeight: 700 }}>{title}</Mui.Typography>
						</Mui.Box>
						<Mui.IconButton size="small" onClick={onClose}>
							<FeatherIcon>
								<Icons.X />
							</FeatherIcon>
						</Mui.IconButton>
					</Mui.Box>
				</Mui.DialogTitle>
				{/* Inset divider — sits directly below the title (above the tabs), aligned
					with the content padding instead of running edge to edge */}
				<Mui.Divider style={{ margin: "0 24px" }} />
				{!isWizard && (
					<Mui.Box style={{ padding: "16px 24px 0" }}>
						<ModalTabs
							value={activeTab}
							onChange={(newValue) => this.setState({ activeTab: newValue })}
						/>
					</Mui.Box>
				)}
				{/* No bottom padding: the sticky footer bars pin to the scrollport edge, and
					container padding would show as a gap with content scrolling through it
					(MAX-4008). Footers carry their own 16px white padding; the Status tab
					(no footer) adds its own bottom padding instead. */}
				<Mui.DialogContent style={{ paddingTop: isWizard ? 16 : 32, paddingBottom: 0 }}>
					{activeTab === 0 && (
						<ConnectionTab
							integration={integration}
							connected={connected}
							webhooksMissing={webhooksMissing}
							webhookLastHealthCheckDate={webhookLastHealthCheckDate}
							agentsMatched={agentsMatched}
							ihfAgentsNotInFub={ihfAgentsNotInFub}
							fubUsersNotInIhf={fubUsersNotInIhf}
							leadsMatched={leadsMatched}
							leadsUnmatched={leadsUnmatched}
							backfillStatus={backfillStatus}
							agentSync={agentSync}
							apiKeyValid={apiKeyValid}
							lastWebhookProcessed={lastWebhookProcessed}
							lastSyncDate={lastSyncDate}
							errorCount24h={errorCount24h}
							backfillLeadsStatus={backfillLeadsStatus}
							backfillEventsStatus={backfillEventsStatus}
							onStatusChange={onStatusChange}
							onClose={onClose}
							cachedSelection={this.state.agentSelection}
							onSelectionLoaded={(agentSelection) => this.setState({ agentSelection })}
						/>
					)}
					{activeTab === 1 && (
						<SettingsTab
							integration={integration}
							agentSync={agentSync}
							mergeStrategy={mergeStrategy ?? "fill_in_blanks"}
							onStatusChange={onStatusChange}
							onClose={onClose}
							cachedSelection={this.state.agentSelection}
							onSelectionLoaded={(agentSelection) => this.setState({ agentSelection })}
						/>
					)}
				</Mui.DialogContent>
			</Mui.Dialog>
		);
	}

}

export const ViewIntegrationModal = Component;
